import React, { useState } from 'react';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import AddIcon from '@atlaskit/icon/glyph/add';
import Button from '@atlaskit/button/standard-button';
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { Flex } from '@atlaskit/primitives';
import isEmpty from 'lodash/isEmpty';
import { NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { ApplyPromocodeSection } from './apply-promocode-section';
import { AppliedPromotions } from '@css-xp/ppc-purchase-experience';
import { useProductPromotionsStore, useStoreActions } from '../../../../store';
import { CartPromotionAction } from '../types';
import { CartProductSchema } from '../../../../types';

export const ViewProductPromotions = ({
  id,
  productId,
  isApp,
  discount,
  isDesignIteration1Enabled,
}: {
  id: string;
  productId: CartProductSchema['productId'];
  isApp: boolean;
  discount?: number | string;
  isDesignIteration1Enabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const appliedPromotions = useProductPromotionsStore({ id, isApp });
  const promocodePromotions = appliedPromotions?.filter((promotion) => promotion?.type === 'PROMO_CODE');
  const { updateEntitlementPromotions } = useStoreActions();
  const { sendSuccessNotification, setNotificationSent } = useNotification();

  const showRemovePromotionSuccessMessage = () => {
    const notificationOptions = successNotificationBuilder()
      .withId('removed-promotion-notification')
      .withHeader('Promotions updated')
      .withMessage('Promotions will be removed when you confirm your order.')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  const removeAppliedPromocode = (promotionId: string) => {
    updateEntitlementPromotions({
      identifier: id,
      promotionId,
      promotionAction: isApp ? CartPromotionAction.REMOVE_APP_PROMOTION : CartPromotionAction.REMOVE_PRODUCT_PROMOTION,
      isApp,
    });
    showRemovePromotionSuccessMessage();
    setIsOpen(false);
  };

  const getTooltipMessage = (isApp: boolean, discount?: number | string) => {
    if (isApp) {
      return Number(discount) > 0 ? 'View details' : 'Add promotion code';
    }
    return 'View details';
  };

  const getIcon = (isApp: boolean, discount?: number | string) => {
    if (isApp) {
      return Number(discount) > 0 ? (
        <WatchIcon label="View details" size="small" />
      ) : (
        <AddIcon label="Add promotion" size="small" />
      );
    }

    return <WatchIcon label="View details" size="small" />;
  };

  return (
    <>
      <Popup
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        placement="bottom-start"
        zIndex={2000}
        content={() => (
          <div className="promotion-main-content">
            <AppliedPromotions
              promotions={appliedPromotions}
              removeAppliedPromocode={removeAppliedPromocode}
              isRemovePromotionsEnabled={true}
              isDiscretionaryDiscountsExcluded={true}
            />
            {isApp && isEmpty(promocodePromotions) && (
              <ApplyPromocodeSection productId={productId} id={id} onClose={() => setIsOpen(false)} />
            )}
          </div>
        )}
        trigger={(triggerProps) =>
          isDesignIteration1Enabled ? (
            <Tooltip content={getTooltipMessage(isApp, discount)} position="bottom-end">
              <Flex alignItems="center" justifyContent="end">
                <Button
                  {...triggerProps}
                  //showAddIcon only if apps doesn't have any discount
                  iconBefore={getIcon(isApp, discount)}
                  onClick={() => setIsOpen(true)}
                  appearance="subtle"
                  label="manage-promotion"
                  className="promotion-button"
                />
              </Flex>
            </Tooltip>
          ) : (
            <Button
              {...triggerProps}
              testId="manage-promotion-button"
              iconBefore={<WatchIcon label="View history" size="small" />}
              onClick={() => setIsOpen(true)}
            />
          )
        }
      />
    </>
  );
};
